import Typography from "@mui/joy/Typography";

import React, { useState, useEffect, useRef } from "react";
import { RiskAssessmentItem } from "../../../models/risk-assessment.interface";
import { Dialog } from "primereact/dialog";
import { RiskItem } from "./components/RiskItem";
import { Button } from "primereact/button";
import ButtonPanel from "../../../components/Shared/ButtonPanel";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { apiGet } from "../../../service/api-service";
import { Toast } from "primereact/toast";

function SiteRiskAssessment() {
  const [selectedItem, setSelectedItem] = useState<RiskAssessmentItem | null>(null);
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const toast = useRef(null as any);

  // Load and flatten the nested API response
  const loadData = async () => {
    setLoading(true);
    try {
      const response = await apiGet("core", "risk-assessment", {
        queryParams: {
          rows: 1000,
          page: 0,
          table_view: true
        }
      });

      const jobSteps = response.content?.job_steps || [];
      const flattened: any[] = [];

      jobSteps.forEach((jobStep: any) => {
        const hazards = jobStep.hazards || [];
        
        // Create a list of all hazard names for this job step
        const allHazardNames = hazards.map((h: any) => h.hazard_name).filter(Boolean).join(", ");
        
        hazards.forEach((hazard: any) => {
          const risks = hazard.risks || [];
          
          risks.forEach((risk: any) => {
            flattened.push({
              ...risk,
              hazard_list: allHazardNames,
              job_step_name: jobStep.job_step_name,
              hazard_name: hazard.hazard_name,
            });
          });
        });
      });

      setData(flattened);
    } catch (error) {
      console.error("Error loading risk assessments:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load risk assessments",
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <div>
      <Typography level="h2" component="h1">
        Risk Assessments
      </Typography>
      
      <div className="card" style={{ marginTop: '1rem' }}>
        <DataTable
          value={data}
          loading={loading}
          dataKey="id"
          paginator
          rows={50}
          rowsPerPageOptions={[10, 20, 50, 100]}
          onRowClick={(e) => setSelectedItem(e.data as RiskAssessmentItem)}
          selectionMode="single"
          scrollable
          scrollHeight="calc(100vh - 300px)"
          emptyMessage="No risk assessments found"
        >
          <Column field="risk_id" header="Risk ID" sortable filter filterPlaceholder="Search by Risk ID" />
          <Column 
            field="review_date" 
            header="Review Date" 
            sortable 
            filter 
            filterPlaceholder="Search by date"
            body={(rowData) => rowData.review_date ? new Date(rowData.review_date).toLocaleDateString() : ""}
          />
          <Column field="hazard_list" header="Hazard" filter filterPlaceholder="Search by hazard" />
          <Column field="likelihood_label" header="Before Likelihood" filter filterPlaceholder="Filter" />
          <Column field="consequence_label" header="Before Consequence" filter filterPlaceholder="Filter" />
          <Column field="rating_label" header="Before Rating" filter filterPlaceholder="Filter" />
          <Column field="after_likelihood_label" header="After Likelihood" filter filterPlaceholder="Filter" />
          <Column field="after_consequence_label" header="After Consequence" filter filterPlaceholder="Filter" />
          <Column field="after_rating_label" header="After Rating" filter filterPlaceholder="Filter" />
        </DataTable>
      </div>

      <Toast ref={toast} />

      {selectedItem && (
        <Dialog
          onHide={() => setSelectedItem(null)}
          header={selectedItem.name}
          visible
          style={{ minWidth: "70vw" }}
        >
          <RiskItem item={selectedItem} />
          <ButtonPanel inModal={true}>
            <Button label={"Close"} onClick={() => setSelectedItem(null)} outlined={true} />
          </ButtonPanel>
        </Dialog>
      )}
    </div>
  );
}

export default SiteRiskAssessment;
